// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import { escapeHtml, toCsv } from './domain/csv';
import { money } from './domain/pricing';
import type { Analysis } from './types';

/**
 * The two artefacts.
 *
 * ONE DOWNLOAD PER EXPLICIT CLICK, never two from one gesture. Chrome's limiter flips the second
 * programmatic download in a single gesture to a permission prompt, and Safari silently suppresses
 * it — so a "download everything" button hands an iPhone user one file and no error. Two buttons.
 *
 * Neither artefact contains a line of the user's session. They carry token counts, dollar figures,
 * tool NAMES and short labels — nothing that was read, written or said. That is a deliberate
 * limit: the whole reason this tool exists in a browser tab is that a session log is the user's
 * entire codebase, and an export that leaked it back out would undo the point.
 */

function pct(x: number): string {
  return `${(x * 100).toFixed(1)}%`;
}

export function ledgerCsv(a: Analysis): string {
  const headers = [
    'rank',
    'turn',
    'kind',
    'what_was_added',
    'tokens',
    're_reads',
    'carry_tokens',
    'carry_usd',
    'write_usd',
    'total_usd',
  ];
  const rows = a.ledger.map((r, i) => [
    String(i + 1),
    r.kind === 'baseline' && r.turnIndex === 1 ? 'baseline' : String(r.turnIndex),
    r.kind,
    r.label,
    String(r.tokens),
    String(r.reReads),
    String(r.carryTokens),
    r.carryCost.toFixed(6),
    r.writeCost.toFixed(6),
    (r.carryCost + r.writeCost).toFixed(6),
  ]);
  return toCsv(headers, rows);
}

export function reportHtml(a: Analysis, headline: string): string {
  const rows = a.ledger
    .slice(0, 60)
    .map((r, i) => {
      const label = r.kind === 'baseline' ? `<strong>${escapeHtml(r.label)}</strong>` : escapeHtml(r.label);
      return `<tr>
  <td class="n">${i + 1}</td>
  <td class="n">${r.kind === 'baseline' && r.turnIndex === 1 ? '—' : r.turnIndex}</td>
  <td>${label}</td>
  <td class="n">${r.tokens.toLocaleString('en-GB')}</td>
  <td class="n">${r.reReads.toLocaleString('en-GB')}</td>
  <td class="n">${r.carryTokens.toLocaleString('en-GB')}</td>
  <td class="n">${escapeHtml(money(r.carryCost + r.writeCost))}</td>
</tr>`;
    })
    .join('\n');

  const repeats = a.repeats
    .slice(0, 20)
    .map(
      (r) => `<tr>
  <td>${escapeHtml(r.label)}</td>
  <td class="n">${r.count}</td>
  <td class="n">${r.bytes.toLocaleString('en-GB')}</td>
  <td class="n">${r.repeatBytes.toLocaleString('en-GB')}</td>
</tr>`,
    )
    .join('\n');

  const buckets = (['read', 'output', 'write1h', 'write5m', 'input'] as const)
    .map(
      (b) => `<tr>
  <td>${b}</td>
  <td class="n">${a.tokensByBucket[b].toLocaleString('en-GB')}</td>
  <td class="n">${escapeHtml(money(a.costByBucket[b]))}</td>
  <td class="n">${a.totalCost > 0 ? pct(a.costByBucket[b] / a.totalCost) : '—'}</td>
</tr>`,
    )
    .join('\n');

  const files = a.sessions
    .map((s) => `<li>${escapeHtml(s.fileName)} — ${s.turns.length.toLocaleString('en-GB')} turns${s.badLines ? `, ${s.badLines} unreadable line(s)` : ''}</li>`)
    .join('\n');

  // Entirely self-contained: no <script>, no <link>, no src=, no remote anything. A test asserts it.
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>runledger report</title>
<style>
  :root { color-scheme: light dark; --fg:#111; --dim:#666; --line:#ddd; --bg:#fff; }
  @media (prefers-color-scheme: dark) { :root { --fg:#e6edf3; --dim:#95a3b2; --line:#28323d; --bg:#0f151c; } }
  body { font: 14px/1.55 -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
         color: var(--fg); background: var(--bg); margin: 0; padding: 2rem 1.25rem 4rem; }
  main { max-width: 60rem; margin: 0 auto; }
  h1 { font-size: 1.35rem; margin: 0 0 .25rem; }
  h2 { font-size: 1rem; margin: 2rem 0 .5rem; }
  .lede { font-size: 1.05rem; margin: 0 0 1.5rem; }
  .dim { color: var(--dim); }
  table { border-collapse: collapse; width: 100%; font-size: 13px; }
  th, td { text-align: left; padding: .35rem .5rem; border-bottom: 1px solid var(--line); vertical-align: top; }
  th { font-weight: 600; color: var(--dim); font-size: 11.5px; text-transform: uppercase; letter-spacing: .04em; }
  td.n, th.n { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .wrap { overflow-x: auto; }
  footer { margin-top: 3rem; color: var(--dim); font-size: 12px; }
</style>
</head>
<body>
<main>
  <h1>runledger report</h1>
  <p class="lede">${escapeHtml(headline)}</p>

  <h2>Where the money went</h2>
  <div class="wrap"><table>
    <thead><tr><th>bucket</th><th class="n">tokens</th><th class="n">cost</th><th class="n">share</th></tr></thead>
    <tbody>${buckets}</tbody>
  </table></div>
  <p class="dim">Total ${escapeHtml(money(a.totalCost))} across ${a.turnCount.toLocaleString('en-GB')} turns.
  Priced from the published list of ${escapeHtml(a.priceTable.asOf)}.</p>

  <h2>Carry ledger — what each turn added, and how many turns re-read it</h2>
  <div class="wrap"><table>
    <thead><tr><th class="n">#</th><th class="n">turn</th><th>what was added</th><th class="n">tokens</th><th class="n">re-reads</th><th class="n">carry tokens</th><th class="n">cost</th></tr></thead>
    <tbody>${rows}</tbody>
  </table></div>

  <h2>Byte-identical repeats</h2>
  ${
    a.repeats.length
      ? `<div class="wrap"><table>
    <thead><tr><th>block</th><th class="n">times</th><th class="n">bytes each</th><th class="n">repeated bytes</th></tr></thead>
    <tbody>${repeats}</tbody>
  </table></div>
  <p class="dim">${pct(a.repeatBytes / Math.max(1, a.appendedBytes))} of appended bytes are byte-identical repeats.
  These are reported in bytes and never in dollars: a turn appends several blocks under one exact
  token count, and splitting that count between them by size would be a guess.</p>`
      : '<p class="dim">No byte-identical repeats found.</p>'
  }

  <h2>How much of the bill this explains</h2>
  <p>The decomposition accounts for <strong>${pct(a.readCoverage)}</strong> of the reported
  cache-read tokens.${a.resetCount ? ` ${a.resetCount} context reset(s) were found — points where the cached prefix was replaced and the whole context was paid for again at write prices.` : ''}</p>

  <h2>Files read</h2>
  <ul>${files}</ul>

  <footer>
    Generated by runledger — https://runledger.benrichardson.dev — entirely in the browser.<br>
    This report contains token counts, tool names and short labels. It contains no line of the
    session itself: no file contents, no commands, no messages.
  </footer>
</main>
</body>
</html>
`;
}

/**
 * The headline.
 *
 * Generated from the analysis rather than transcribed, so it cannot drift from the table beneath
 * it. It leads with the baseline, because measured across 118 real sessions the median session
 * carries 61,373 tokens before the user's first word and that block is re-read on every turn.
 */
export function headlineFor(a: Analysis): string {
  if (a.turnCount === 0) return 'No priced turns were found in these files.';

  const parts: string[] = [];
  const base = a.ledger.find((r) => r.kind === 'baseline' && r.turnIndex === 1);
  const share = base && a.totalCost > 0 ? (base.carryCost + base.writeCost) / a.totalCost : 0;

  parts.push(
    `Before the first reply this run already carried ${a.baselineTokens.toLocaleString('en-GB')} tokens — ` +
      `the system prompt, the tool schemas and your project instructions.`,
  );
  if (base && base.reReads > 0) {
    parts.push(
      `That block was re-read on ${base.reReads.toLocaleString('en-GB')} later turns: ` +
        `${base.carryTokens.toLocaleString('en-GB')} cache-read tokens, ` +
        `${money(base.carryCost + base.writeCost)}${share > 0 ? ` — ${pct(share)} of the ${money(a.totalCost)} total` : ''}.`,
    );
  }

  // The baseline is usually the biggest single line, but not always — a cache expiry re-writes the
  // whole context at write prices and can beat it outright. Say whichever is actually true rather
  // than the one that makes the better slogan.
  const top = a.ledger[0];
  if (top && top !== base && a.totalCost > 0) {
    const topShare = (top.carryCost + top.writeCost) / a.totalCost;
    const what =
      top.kind === 'baseline'
        ? `the context being rebuilt at turn ${top.turnIndex}`
        : `what turn ${top.turnIndex} added (${top.label})`;
    parts.push(
      `The single most expensive line was ${what}: ` +
        `${money(top.carryCost + top.writeCost)}, ${pct(topShare)} of the total.`,
    );
  }

  return parts.join(' ');
}
